import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Package } from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";
import PushNotificationToggle from "../components/PushNotificationToggle";

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to fetch orders');
      }
      setLoading(false);
    };
    fetchOrders();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h1 className="font-heading text-5xl tracking-wide">YOUR ORDERS</h1>
        </div>
        {/* Push notification opt-in */}
        <div className="mb-6">
          <PushNotificationToggle />
        </div>
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D62828] mx-auto"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16" data-testid="no-orders">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-6">No orders yet</p>
            <Link to="/products" className="inline-block bg-[#D62828] hover:bg-[#B91C1C] text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wider">Start Shopping</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((order) => (
              <Link
                key={order.order_id}
                to={`/orders/${order.order_id}`}
                className="block bg-[#121212] border border-white/5 rounded-sm p-6 hover:border-[#D62828]/50 transition-colors"
                data-testid={`order-${order.order_id}`}
              >
                <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
                  <div>
                    <p className="text-gray-400 text-sm">Order #{order.order_id}</p>
                    <p className="text-gray-500 text-xs">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-sm text-xs font-semibold uppercase ${order.order_status === 'delivered' ? 'bg-green-500/20 text-green-400' : order.order_status === 'confirmed' ? 'bg-green-500/20 text-green-400' : order.order_status === 'shipped' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {order.order_status}
                    </span>
                    <span className="text-[#D62828] font-bold">${order.total.toFixed(2)}</span>
                  </div>
                </div>
                <div className="border-t border-white/5 pt-4 flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {order.items.map((item) => (
                      <span key={item.product_id} className="text-gray-400 text-sm">{item.name} x{item.quantity}</span>
                    ))}
                  </div>
                  <span className="text-[#D62828] text-xs uppercase tracking-wide font-bold whitespace-nowrap">Track →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
